import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import InventoryService from '../../services/InventoryService';
import analytics from '../../services/AnalyticsService';
import usePageView from '../../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';
import AssetForm from '../../components/v2/AddAsset/AssetForm';
import SmartAutofillSlots from '../../components/v2/AddAsset/SmartAutofillSlots';
import ItemImageGallery from '../../components/v2/AddAsset/ItemImageGallery';
import ItemAutofillPanel from '../../components/v2/AddAsset/ItemAutofillPanel';
import {
    applyAutofillSuggestions,
    mapScanToForm,
    splitPromotedKeys,
} from '../../components/v2/AddAsset/scanMapping';
import { toast as Toast, ConfirmationModal } from '../../components/v2/V2Components';
import { sanitizeNumeric } from '../../utils/numericInput';


// Defined at module scope so the reference is stable across renders.
// This prevents the JSON.stringify dirty-check from racing against
// a new object created on every render.
const INITIAL_FORM_STATE = {
    name: '',
    manufacturer: '',
    category: '',
    item_subcategory: '',
    model: '',
    serial: '',
    room_id: '',
    date: '',
    value: '',
    color: '',
    notes: '',
    properties: {}
};

const AddAsset = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = !!id;

    usePageView(ANALYTICS_EVENTS.INVENTORY_ASSET_FORM_VIEWED, { is_edit_mode: isEditMode });

    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(isEditMode);
    const [isDirty, setIsDirty] = useState(false);
    const [pendingDiscardDest, setPendingDiscardDest] = useState(null);
    const [rooms, setRooms] = useState([]);

    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [scannedFiles, setScannedFiles] = useState([]);
    // In edit mode, keep the live image list so the gallery can render and
    // refresh independently of the form fields.
    const [editItemImages, setEditItemImages] = useState([]);
    // Pristine state for dirty-checking in edit mode: stashed after fetch completes
    // so we compare against the loaded item, not the blank INITIAL_FORM_STATE.
    const pristineRef = useRef(INITIAL_FORM_STATE);
    // Timestamp of the most recent scan completion. Powers `ms_since_scan` on
    // save/discard events so we can measure how long users spend reviewing
    // AI-extracted data before committing.
    const scanCompletedAtRef = useRef(null);

    // Start a new analytics session per AddAsset mount. All scan/save/discard
    // events from this mount carry the same scan_session_id so we can join
    // them into a single funnel in the analytics tool.
    useEffect(() => {
        analytics.startSession();
        return () => { scanCompletedAtRef.current = null; };
    }, []);

    // Fetch rooms for the location dropdown
    useEffect(() => {
        let cancelled = false;
        InventoryService.getRooms()
            .then(data => { if (!cancelled) setRooms(Array.isArray(data) ? data : []); })
            .catch(err => console.error('Failed to load rooms', err));
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (isEditMode) {
            const fetchItem = async () => {
                try {
                    const item = await InventoryService.getItem(id);
                    // Hoist any legacy properties.color into the top-level field
                    // for items created before the PR #180 backfill migration
                    // ran. Top-level wins on conflict.
                    const { promoted, remaining } = splitPromotedKeys(item.properties);
                    const loaded = {
                        name: item.name || '',
                        manufacturer: item.manufacturer || '',
                        category: item.item_category || '',
                        item_subcategory: item.item_subcategory || '',
                        model: item.model_number || '',
                        serial: item.serial_number || '',
                        date: item.purchase_date ? item.purchase_date.substring(0, 10) : '',
                        value: item.purchase_price != null ? String(item.purchase_price) : '',
                        room_id: item.room_id ? String(item.room_id) : '',
                        color: item.color || promoted.color || '',
                        notes: item.notes || '',
                        properties: remaining
                    };
                    setFormData(loaded);
                    pristineRef.current = loaded;
                    setEditItemImages(item.images || []);
                } catch (error) {
                    console.error("Failed to fetch item:", error);
                    Toast.error("Failed to load item for editing.");
                } finally {
                    setIsLoading(false);
                }
            };
            fetchItem();
        }
    }, [id, isEditMode]);

    const refreshItemImages = useCallback(async () => {
        if (!isEditMode) return;
        try {
            const item = await InventoryService.getItem(id);
            setEditItemImages(item.images || []);
        } catch (err) {
            console.error('Failed to refresh item images', err);
        }
    }, [id, isEditMode]);

    // Track Form changes for dirty state.
    // scannedFiles are intentionally excluded — they are transient upload state,
    // not unsaved form data. When a scan populates formData, the dirty-check
    // triggers naturally via the formData dependency.
    useEffect(() => {
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(pristineRef.current);
        setIsDirty(hasChanges);
    }, [formData]);

    // Handle Browser Close / Refresh
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    const handleNavigation = (destination = '/inventory') => {
        if (isDirty) {
            setPendingDiscardDest(destination);
        } else {
            navigate(destination);
        }
    };

    const confirmDiscard = () => {
        const dest = pendingDiscardDest;
        const filledCount = Object.entries(formData).filter(([k, v]) => {
            if (k === 'properties') return v && Object.keys(v).length > 0;
            return v !== '' && v != null;
        }).length;
        analytics.track('inventory.item_discarded', {
            app_flow: 'add_asset',
            was_scan_assisted: scanCompletedAtRef.current != null,
            was_dirty: true,
            fields_filled_count: filledCount,
            ms_since_scan: scanCompletedAtRef.current
                ? Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - scanCompletedAtRef.current)
                : undefined,
            dest: dest ?? '/inventory',
        });
        setPendingDiscardDest(null);
        setIsDirty(false);
        navigate(dest ?? '/inventory');
    };

    const handleFormChange = useCallback((newData) => {
        setFormData(prev => ({ ...prev, ...newData }));
    }, []);

    const handleScanData = useCallback((data, selectedFiles) => {
        setScannedFiles(selectedFiles);

        // Scanner keys → form fields and extra properties (shared with the
        // edit-page re-scan in ItemAutofillPanel).
        const { fields, properties: scannedProperties } = mapScanToForm(data);

        // Merge over the current properties, then hoist a `color` the user
        // typed as a custom property into the top-level field too.
        const { promoted: promotedFromProperties, remaining: mergedProperties } = splitPromotedKeys({
            ...formData.properties,
            ...scannedProperties,
        });

        // Which form fields the scan actually populated. Used to measure
        // extraction quality without sending raw values.
        const fieldsPopulated = Object.entries(fields)
            .filter(([k, v]) => k !== 'color' && v !== '')
            .map(([k]) => k);

        if (fieldsPopulated.length > 0 || Object.keys(mergedProperties).length > Object.keys(formData.properties).length) {
            scanCompletedAtRef.current = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        }

        analytics.track('inventory.scan_form_populated', {
            app_flow: 'add_asset',
            fields_populated: fieldsPopulated,
            item_category: data.item_category || data.category,
            item_subcategory: data.item_subcategory,
            manufacturer: data.manufacturer || data.brand,
        });

        // Scanned values win; fields the scan left empty keep what the user
        // already typed. Color precedence: scan, then a custom `color`
        // property, then the current field.
        setFormData(prev => {
            const next = { ...prev, properties: mergedProperties };
            Object.entries(fields).forEach(([key, value]) => {
                if (value) next[key] = value;
            });
            if (!fields.color && promotedFromProperties.color) next.color = promotedFromProperties.color;
            return next;
        });
    }, [formData.properties]);

    // Edit mode: merge the suggestions the user ticked in ItemAutofillPanel.
    // Only the form changes — the item is saved by Save Changes as usual.
    const handleApplyAutofill = useCallback((suggestions) => {
        scanCompletedAtRef.current = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        setFormData(prev => applyAutofillSuggestions(prev, suggestions));
    }, []);

    const handleSave = async () => {
        if (!formData.name) {
            Toast.error('Item Name is required');
            return;
        }
        if (!formData.category) {
            Toast.error('Category is required');
            return;
        }

        setIsSaving(true);

        try {
            // Sanitize value so currency input like "$1,200.00" parses cleanly instead of NaN.
            const rawValue = formData.value ? String(formData.value).replace(/[^0-9.-]/g, '') : '';

            // Build payload — send room_id as an integer when selected,
            // otherwise omit it so the API falls back to "Unassigned".
            const parsedRoomId = formData.room_id ? parseInt(formData.room_id, 10) : null;

            // Last-line-of-defense scrub: if the user typed `color` as a custom
            // property in the AssetForm grid, hoist it to top-level and drop it
            // from properties. The backend validator (PR #180) would 400 otherwise.
            const { promoted: lastMinutePromoted, remaining: cleanProperties } = splitPromotedKeys(formData.properties);
            const finalColor = formData.color || lastMinutePromoted.color || null;

            const payload = {
                name: formData.name,
                manufacturer: formData.manufacturer,
                item_category: formData.category,
                item_subcategory: formData.item_subcategory || null,
                model_number: formData.model,
                serial_number: formData.serial,
                purchase_date: formData.date || null,
                // sanitizeNumeric rejects malformed input ("12-34" → null)
                // instead of silently truncating, per CodeRabbit finding on PR #171.
                purchase_price: sanitizeNumeric(rawValue),
                color: finalColor,
                notes: formData.notes,
                properties: cleanProperties
            };

            // Include room_id so the backend assigns the item to the correct room.
            // For create, also keep "location" as empty so the backend doesn't
            // auto-create a room from a free-text name.
            if (parsedRoomId) {
                payload.room_id = parsedRoomId;
            }

            let savedItem;
            if (isEditMode) {
                savedItem = await InventoryService.updateItem(id, payload);
            } else {
                savedItem = await InventoryService.createItem(payload);
            }

            const savedAt = (typeof performance !== 'undefined' ? performance.now() : Date.now());
            const imageCountBySlot = scannedFiles.reduce((acc, e) => {
                acc[e.slot] = (acc[e.slot] || 0) + 1;
                return acc;
            }, {});
            let uploadSuccessCount = 0;
            let uploadFailCount = 0;

            if (!isEditMode && scannedFiles && scannedFiles.length > 0 && savedItem && savedItem.id) {
                // scannedFiles is now an array of { slot, file } from SmartAutofillSlots.
                // The backend auto-promotes the first photo to primary, so we don't
                // need to send is_primary on the client anymore.
                const uploadPromises = scannedFiles.map(entry =>
                    InventoryService.uploadItemImage(savedItem.id, entry.file, false, entry.slot)
                        .then(() => {
                            analytics.track('inventory.image_uploaded', {
                                app_flow: 'add_asset',
                                slot_type: entry.slot,
                                size_kb: Math.round((entry.file.size || 0) / 1024),
                                mime_type: entry.file.type || 'unknown',
                                success: true,
                                ms_since_save: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - savedAt),
                            });
                            uploadSuccessCount += 1;
                        })
                        .catch((err) => {
                            const errorCode = err?.response?.data?.error_code || (err?.response?.status ? `http_${err.response.status}` : 'network_error');
                            analytics.track('inventory.image_upload_failed', {
                                app_flow: 'add_asset',
                                slot_type: entry.slot,
                                size_kb: Math.round((entry.file.size || 0) / 1024),
                                error_code: errorCode,
                            });
                            uploadFailCount += 1;
                            throw err;
                        })
                );

                const results = await Promise.allSettled(uploadPromises);

                const failures = results.filter(r => r.status === 'rejected');
                if (failures.length > 0) {
                    console.error("Failed to upload some image(s):", failures);
                    if (failures.length === scannedFiles.length) {
                        Toast.info('Item saved, but all image uploads failed.');
                    } else {
                        Toast.info(`Item saved, but ${failures.length} of ${scannedFiles.length} images failed to upload.`);
                    }
                } else {
                    Toast.success('Asset saved');
                }
            } else {
                Toast.success('Asset saved');
            }

            const filledCount = Object.entries(formData).filter(([k, v]) => {
                if (k === 'properties') return v && Object.keys(v).length > 0;
                return v !== '' && v != null;
            }).length;
            analytics.track('inventory.item_saved', {
                app_flow: 'add_asset',
                is_edit_mode: isEditMode,
                item_category: formData.category || undefined,
                item_subcategory: formData.item_subcategory || undefined,
                manufacturer: formData.manufacturer || undefined,
                was_scan_assisted: scanCompletedAtRef.current != null,
                ms_since_scan: scanCompletedAtRef.current
                    ? Math.round(savedAt - scanCompletedAtRef.current)
                    : undefined,
                fields_filled_count: filledCount,
                image_count_by_slot: imageCountBySlot,
                image_upload_success_count: uploadSuccessCount,
                image_upload_fail_count: uploadFailCount,
            });

            setIsDirty(false);
            navigate('/inventory');
        } catch (error) {
            console.error("Failed to save item", error);
            Toast.error('Failed to save item. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-[var(--bg-main)] text-[var(--text-main)] pb-24 md:pb-12 font-['Plus_Jakarta_Sans']">
            <main className="max-w-6xl mx-auto px-6 md:px-12 mt-8 md:mt-12">
                <div className="mb-8 p-1">
                    <span className="text-xs font-bold tracking-widest text-[var(--text-muted)] uppercase mb-2 block font-['Plus_Jakarta_Sans']">
                        {isEditMode ? 'Edit Entry' : 'New Entry'}
                    </span>
                    <h2 className="text-3xl md:text-4xl font-extrabold text-[var(--primary)] font-['Manrope']">
                        {isEditMode ? 'Edit Asset' : 'Add New Asset'}
                    </h2>
                </div>


                <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-start">

                    {/* Left: Form */}
                    <div className="w-full lg:flex-[2] space-y-8 order-2 lg:order-1">
                        <AssetForm
                            formData={formData}
                            onChange={handleFormChange}
                            rooms={rooms}
                        />

                        <div className="pt-6 flex flex-col md:flex-row gap-4">
                            <button
                                onClick={handleSave}
                                disabled={isSaving || isLoading}
                                className="w-full md:w-auto px-10 py-4 bg-[var(--primary)] text-[var(--primary-text)] rounded-full font-bold text-lg hover:bg-[var(--primary-container)] transition-all shadow-xl shadow-[color-mix(in_srgb,var(--primary),transparent_90%)] flex items-center justify-center gap-2 disabled:opacity-70"
                            >
                                {isSaving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Save Asset')}
                            </button>
                            <button
                                onClick={() => handleNavigation()}
                                disabled={isSaving}
                                className="w-full md:w-auto px-10 py-4 bg-transparent text-[var(--secondary)] border border-[var(--border)] rounded-full font-bold text-lg hover:bg-[var(--bg-hover)] transition-all"
                            >
                                Cancel
                            </button>
                        </div>
                        <p className="text-center md:text-left text-xs text-[var(--text-muted)] font-medium opacity-60 mt-4">All data secured with 256-bit encryption</p>
                    </div>

                    {/* Right: Autofill (create mode) or Image Gallery + re-scan Autofill (edit mode) */}
                    <div className="w-full lg:flex-[1] order-1 lg:order-2">
                        {!isEditMode ? (
                            <SmartAutofillSlots onScanSuccess={handleScanData} />
                        ) : (
                            <div className="bg-[var(--bg-card)] p-6 rounded-3xl shadow-[var(--shadow)] border border-[var(--border)]">
                                <h3 className="text-xl font-bold text-[var(--primary)] font-['Manrope'] mb-4">Images &amp; Documents</h3>
                                <ItemImageGallery
                                    itemId={parseInt(id, 10)}
                                    images={editItemImages}
                                    onChange={refreshItemImages}
                                />
                                <div className="border-t border-[var(--border)] mt-6 pt-6">
                                    {/* Keyed by item so navigating to another item's edit
                                        page drops an in-flight scan and any open review —
                                        otherwise the old item's suggestions could be applied
                                        to the new one. */}
                                    <ItemAutofillPanel
                                        key={id}
                                        itemId={id}
                                        fileCount={editItemImages.length}
                                        formData={formData}
                                        onApply={handleApplyAutofill}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            </main>

            <ConfirmationModal
                isOpen={pendingDiscardDest !== null}
                onClose={() => setPendingDiscardDest(null)}
                onConfirm={confirmDiscard}
                title="Discard unsaved changes?"
                message="You have unsaved changes that will be lost if you leave this page."
                confirmText="Discard"
                cancelText="Keep editing"
                isDanger
            />
        </div>
    );
};

export default AddAsset;
